import { useState, useImperativeHandle, forwardRef, useRef, React, useEffect } from 'react';
import styled from 'styled-components';

const GoogleAddressInput = forwardRef((props, ref) => {
  const { className, placeholder, type, clickCountFn, clicked } = props;

  const [value, setValue] = useState('');
  const [changed, setChanged] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [defaultPlaceholder, setDefaultPlaceholder] = useState(placeholder);
  const [fontSize, setFontSize] = useState(16);

  const inputRef = useRef();
  const measureRef = useRef();

  useImperativeHandle(ref, () => ({
    setValue: (val) => {
      setValue(val);
      setChanged(true);
      if (val !== undefined && inputRef.current) inputRef.current.style.borderColor = '#E8E8E8';
    },
    getValue: () => {
      if (changed) return value;
      return '';
    },
    plusClickCount: () => {
      setClickCount((c) => c + 1);
    },
    getClickCount: () => {
      return clickCount;
    },
    setDefaultPlaceholder: (val) => {
      setDefaultPlaceholder(val);
      if (inputRef.current) inputRef.current.style.borderColor = '#FF3131';
    },
    reset: (val) => {
      setClickCount(0);
      setDefaultPlaceholder(val);
      if (inputRef.current) inputRef.current.style.borderColor = '#E8E8E8';
    },
  }));

  useEffect(() => {
    clickCountFn(className, clickCount);
  }, [clickCount, value]);

  useEffect(() => {
    if (!measureRef.current || !inputRef.current) return;

    const inputWidth = inputRef.current.offsetWidth - 10;
    measureRef.current.style.fontSize = '16px';

    const textWidth = measureRef.current.offsetWidth;

    if (textWidth > inputWidth && value) setFontSize(12);
    else setFontSize(16);
  }, [value]);

  function handleClick() {
    if (!inputRef.current) return;
    inputRef.current.style.borderColor = '#FF8B48';
    setDefaultPlaceholder('');
  }

  function handleFocus() {
    clicked();
  }

  function handleBlur() {
    setClickCount((c) => c + 1);

    if (value === undefined) {
      setDefaultPlaceholder(placeholder);
      if (inputRef.current) inputRef.current.style.borderColor = '#FF3131';
    }
    else {
      if (inputRef.current) inputRef.current.style.borderColor = '#E8E8E8';
    }
  }

  return (
    <>
      <MeasureSpan ref={measureRef}>{value}</MeasureSpan>
      <Input
        ref={inputRef}
        clickCount={clickCount}
        fontSize={fontSize}
        type={type}
        tabIndex={-1}
        placeholder={defaultPlaceholder}
        value={value}
        className={`${className} ${changed ? 'changed' : ''}`}
        onClick={handleClick}
        onFocus={handleFocus}
        onBlur={handleBlur}
        readOnly
      />
    </>
  );
});

export default GoogleAddressInput;

const MeasureSpan = styled.span`
  position: absolute;
  visibility: hidden;
  white-space: nowrap;
  font-family: NotoSansKR-Regular;
  font-size: 16px;
`;

const Input = styled.input`
  font-family: NotoSansKR-Regular;
  font-size: ${(props) => props.fontSize}px;

  width: 100%;
  height: 16px;
  padding: 0 0 4px 0;
  margin-top: max(12px, 0.8333333333333333vw);

  outline: none;
  border-width: 0 0 1px 0;
  border-color: #e8e8e8;
  color: #000000;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ::placeholder {
    color: ${(props) => (props.clickCount < 1 || props.value !== '' ? `#CBCBCB` : `#FF3131;`)};
  }

  transition: all 0.5s ease-in-out;
`;
